import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Tabs,
  Tab,
  Box,
  Chip,
  LinearProgress,
  TextField,
  Paper,
  Button,
  Divider
} from '@mui/material';
import { Star, CalendarToday } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import '../styles/ContractDetails.css';

// Helper function to convert points to hours
const convertToHours = (points, dollarPerHour) => {
  if (!points || !dollarPerHour || dollarPerHour === 0) return 0;
  return ((parseFloat(points) / parseFloat(dollarPerHour)) * 100).toFixed(2);
};

const ClientDeliverables = () => {
  const { contractId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('All');
  
  // Use -30 to +30 days range as default
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
  
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(thirtyDaysFromNow);

  // Status options for filtering
  const statusOptions = ['All', 'Working', 'Waiting on Client', 'Delivered'];

  // Helper function to format dates without date-fns
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Helper function to format date for display
  const formatDateForDisplay = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: 'numeric' 
    });
  };

  // Helper function to convert date to input format (YYYY-MM-DD)
  const toInputDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Helper function to convert input date string to Date object
  const fromInputDate = (dateString) => {
    return new Date(dateString + 'T00:00:00');
  };

  // Contract data state
  const [contract, setContract] = useState(null);
  const [contractLoading, setContractLoading] = useState(true);
  const [contractError, setContractError] = useState(null);

  useEffect(() => {
    fetchTasks();
  }, [contractId]);

  // Fetch contract details for display type and dollar per hour
  useEffect(() => {
    setContractLoading(true);
    setContractError(null);
    const apiBaseUrl = process.env.REACT_APP_API_URL || '';
    fetch(`${apiBaseUrl}/api/contracts/${contractId}`)
      .then(res => res.json())
      .then(data => {
        console.log('Contract data for deliverable conversion:', data);
        setContract(data);
      })
      .catch(err => {
        console.error('Failed to load contract details:', err);
        setContractError('Failed to load contract details');
      })
      .finally(() => setContractLoading(false));
  }, [contractId]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/client-dashboard/tasks/all/${contractId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch tasks');
      }

      const allTasks = await response.json();
      console.log('All tasks data:', allTasks);
      console.log('Total tasks count:', allTasks.length);
      
      // Debug: Check list types and statuses
      const listTypes = [...new Set(allTasks.map(task => task.list_type))];
      const statuses = [...new Set(allTasks.map(task => task.status))];
      console.log('Available list types:', listTypes);
      console.log('Available statuses:', statuses);
      
      // Filter for deliverables only and allowed statuses
      const deliverableTasks = allTasks.filter(task => {
        const matchesListType = task.list_type === 'deliverables';
        const matchesStatus = ['working', 'waiting on client', 'delivered'].includes(task.status.toLowerCase());
        const notDeleted = !task.is_deleted;
        const notArchived = !task.is_archived;
        
        console.log(`Task "${task.name}": list_type=${task.list_type}, status=${task.status}, deleted=${task.is_deleted}, archived=${task.is_archived}`);
        console.log(`  - Matches list type: ${matchesListType}`);
        console.log(`  - Matches status: ${matchesStatus}`);
        console.log(`  - Not deleted: ${notDeleted}`);
        console.log(`  - Not archived: ${notArchived}`);
        
        return matchesListType && matchesStatus && notDeleted && notArchived;
      });
      
      console.log('Filtered deliverable tasks:', deliverableTasks);
      console.log('Deliverable tasks count:', deliverableTasks.length);

      setTasks(deliverableTasks);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load deliverables');
    } finally {
      setLoading(false);
    }
  };

  // Filter tasks based on selected status and date range
  const filteredTasks = tasks.filter(task => {
    // Status filter
    if (selectedStatus !== 'All') {
      const statusMap = {
        'Working': 'working',
        'Waiting on Client': 'waiting on client', 
        'Delivered': 'delivered'
      };
      if (task.status.toLowerCase() !== statusMap[selectedStatus]) {
        return false;
      }
    }

    // Date filter (based on due_date)
    if (task.due_date) {
      const taskDueDate = new Date(task.due_date);
      if (taskDueDate < startDate || taskDueDate > endDate) {
        return false;
      }
    }

    return true;
  }).sort((a, b) => {
    // Sort by due date descending (newest first)
    if (!a.due_date && !b.due_date) return 0;
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return new Date(b.due_date) - new Date(a.due_date);
  });

  // Calculate points by status
  const getPointsByStatus = (status) => {
    const statusTasks = filteredTasks.filter(task => {
      if (status === 'All') return true;
      // Map display status to database status
      const statusMap = {
        'Delivered': 'delivered',
        'Working': 'working',
        'Waiting on Client': 'waiting on client'
      };
      return task.status.toLowerCase() === statusMap[status];
    });
    const totalPoints = statusTasks.reduce((sum, task) => sum + (task.points || 0), 0);
    
    // Convert to hours if contract display type is 'hours'
    if (contract?.customer_display_type === 'hours' && contract?.dollar_per_hour) {
      return convertToHours(totalPoints, contract.dollar_per_hour);
    }
    return totalPoints;
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'working': return '#2196F3'; // Blue
      case 'waiting on client': return '#FFC107'; // Yellow
      case 'delivered': return '#8A42EC'; // Purple
      default: return '#757575';
    }
  };

  const renderTaskCard = (task) => (
    <Paper key={task.task_id} sx={{ mb: 2, p: 2 }}>
      <Typography variant="subtitle1" fontWeight={600}>{task.name}</Typography>
      {task.external_description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {task.external_description}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', mt: 1.5, mb: 1.5 }}>
        <Chip 
          icon={<Star sx={{ color: '#FFD700' }} />} 
          label={`${
            contract?.customer_display_type === 'hours' && contract?.dollar_per_hour 
              ? convertToHours(task.points ?? 0, contract.dollar_per_hour)
              : task.points ?? 0
          } ${contract?.customer_display_type === 'hours' ? 'Hours' : 'Points'}`} 
          size="small" 
          sx={{ px: 1.5, py: 2 }} 
        />
        {task.due_date && (
          <Chip 
            icon={<CalendarToday sx={{ color: '#8A42EC' }} />} 
            label={`Due: ${formatDate(task.due_date)}`} 
            size="small" 
            sx={{ px: 1.5, py: 2 }} 
          />
        )}
        {task.status.toLowerCase() === 'delivered' && task.date_done && (
          <Chip 
            icon={<CalendarToday sx={{ color: '#8A42EC' }} />} 
            label={`Completed: ${formatDate(task.date_done)}`} 
            size="small" 
            sx={{ px: 1.5, py: 2 }} 
          />
        )}
        <Chip 
          label={task.status} 
          size="small" 
          sx={{ 
            backgroundColor: getStatusColor(task.status), 
            color: 'white',
            fontWeight: '500',
            px: 1.5, 
            py: 2
          }}
        />
        {task.link_to_review_tool && (
          <Button 
            href={task.link_to_review_tool} 
            target="_blank" 
            size="small" 
            startIcon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} className="client-dashboard-launch-icon" />} 
            sx={{ minWidth: 0, px: 1.5, py: 2, color: '#8A42EC', fontWeight: 700, textTransform: 'none' }}
          >
            Review
          </Button>
        )}
        {task.link_to_final_deliverable && (
          <Button 
            href={task.link_to_final_deliverable} 
            target="_blank" 
            size="small" 
            startIcon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} className="client-dashboard-launch-icon" />} 
            sx={{ minWidth: 0, px: 1.5, py: 2, color: '#8A42EC', fontWeight: 700, textTransform: 'none' }}
          >
            Final deliverable
          </Button>
        )}
      </Box>
    </Paper>
  );

  return (
    <>
      {/* Status Filter Tabs */}
      <Card className="details-card" style={{ marginBottom: '20px' }}>
        <Tabs
          value={selectedStatus}
          onChange={(event, newValue) => setSelectedStatus(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {statusOptions.map((status) => (
            <Tab
              key={status}
              label={status}
              value={status}
            />
          ))}
        </Tabs>
      </Card>

      {/* Date Filter + Points Cards */}
      <Card className="details-card" style={{ marginBottom: '20px' }}>
        <CardHeader title="Filter" />
        <Divider />
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            {/* Date Filter */}
            <Grid item xs={12} md={8}>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Start Date</label>
                  <input
                    type="date"
                    value={toInputDate(startDate)}
                    onChange={(e) => setStartDate(fromInputDate(e.target.value))}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      minWidth: '150px',
                      backgroundColor: 'white'
                    }}
                  />
                </div>
                <Typography style={{ color: '#666', fontSize: '14px', marginTop: '16px' }}>to</Typography>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>End Date</label>
                  <input
                    type="date"
                    value={toInputDate(endDate)}
                    onChange={(e) => setEndDate(fromInputDate(e.target.value))}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      minWidth: '150px',
                      backgroundColor: 'white'
                    }}
                  />
                </div>
                <Typography variant="body2" style={{ color: '#666', marginLeft: '8px', marginTop: '16px' }}>
                  Showing: {formatDateForDisplay(startDate)} - {formatDateForDisplay(endDate)}
                </Typography>
              </div>
            </Grid>
            
            {/* Points Summary Cards */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <div className="stat-card" style={{ height: 80, minWidth: 120 }}>
                  <div className="stat-value" style={{ fontSize: '24px' }}>{getPointsByStatus('Delivered')}</div>
                  <div className="stat-label">
                    {contract?.customer_display_type === 'hours' ? 'Hours Delivered' : 'Points Delivered'}
                  </div>
                </div>
                <div className="stat-card" style={{ height: 80, minWidth: 120 }}>
                  <div className="stat-value" style={{ fontSize: '24px' }}>{getPointsByStatus('Working')}</div>
                  <div className="stat-label">
                    {contract?.customer_display_type === 'hours' ? 'Hours in Working' : 'Points in Working'}
                  </div>
                </div>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && <LinearProgress style={{ marginBottom: '20px' }} />}

      {/* Error State */}
      {error && (
        <Card style={{ padding: '20px', textAlign: 'center', marginBottom: '20px' }}>
          <Typography color="error">{error}</Typography>
        </Card>
      )}

      {/* Tasks Section */}
      {!loading && !error && (
        <Card className="details-card">
          <CardHeader title="Deliverable Tasks" />
          <Divider />
          <CardContent>
            {filteredTasks.length > 0 ? (
              <Box>
                {filteredTasks.map(renderTaskCard)}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6, color: '#888' }}>
                <Typography variant="h6" style={{ color: '#666', marginBottom: '8px' }}>
                  No deliverables found
                </Typography>
                <Typography variant="body2" style={{ color: '#999' }}>
                  {selectedStatus !== 'All' 
                    ? `No deliverables with status "${selectedStatus}" in the selected date range.`
                    : 'No deliverables found in the selected date range.'
                  }
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default ClientDeliverables; 